import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Avatar } from "primereact/avatar";
import { Tag } from "primereact/tag";
import { Chips } from "primereact/chips";
import { TabView, TabPanel } from "primereact/tabview";
import { supportApi } from "../../services";
import essApi from "../../services/axios/essApi";
import { toast } from "react-toastify";
import { Dropdown } from "primereact/dropdown";

const ICON_OPTIONS = [
  "pi-headphones",
  "pi-cog",
  "pi-dollar",
  "pi-calendar",
  "pi-users",
  "pi-file",
  "pi-shield",
  "pi-desktop",
  "pi-envelope",
  "pi-chart-bar",
  "pi-briefcase",
  "pi-home",
  "pi-lock",
  "pi-globe",
  "pi-star",
];

const empty = {
  name: "",
  icon: "pi-headphones",
  description: "",
  isActive: true,
  owners: [],
  departmentId: null,
};

const emptySubCat = { name: "", description: "" };

const CategoryMaster = ({ departmentId }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subCatDialog, setSubCatDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [subCatForm, setSubCatForm] = useState(emptySubCat);
  const [subCatSaving, setSubCatSaving] = useState(false);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [manualOwners, setManualOwners] = useState([]);

  // ── Department owners state ──
  const [deptOwnerDialog, setDeptOwnerDialog] = useState(false);
  const [deptOwnerForm, setDeptOwnerForm] = useState({ id: null, title: "", owners: [], manualOwners: [] });
  const [deptOwnerSaving, setDeptOwnerSaving] = useState(false);
  const [deptManualOwners, setDeptManualOwners] = useState([]);

  const openDeptOwnerEdit = (dept) => {
    setDeptOwnerForm({
      id: dept.id,
      title: dept.title,
      owners: (dept.owners ?? []).map(Number),
      manualOwners: dept.manualOwners ?? [],
    });
    setDeptManualOwners(dept.manualOwners ?? []);
    setDeptOwnerDialog(true);
  };

  const onDeptOwnerSave = async () => {
    setDeptOwnerSaving(true);
    try {
      await essApi.put(`/department/${deptOwnerForm.id}`, {
        id: deptOwnerForm.id,
        owners: deptOwnerForm.owners,
        manualOwners: deptManualOwners,
      });
      const res = await supportApi.getDepartments();
      setDepartments((Array.isArray(res.data?.data) ? res.data.data : []).map((d) => ({
        label: d.title, value: d.id, owners: d.owners ?? [], manualOwners: d.manualOwners ?? [],
      })));
      setDeptOwnerDialog(false);
      toast.success("Department owners saved");
    } catch {
      toast.error("Failed to save department owners");
    } finally {
      setDeptOwnerSaving(false);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const load = () => {
    setLoading(true);
    supportApi
      .getCategories()
      .then((res) =>
        setCategories(Array.isArray(res.data?.data) ? res.data.data : []),
      )
      .catch(() => toast.error("Failed to load categories"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    supportApi
      .getUsers()
      .then((res) => {
        const usrList = Array.isArray(res.data?.data) ? res.data.data : [];
        const mapped = usrList.map((u) => ({
          label:
            `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() +
            (u.email ? ` (${u.email})` : ""),
          value: Number(u.id),
          email: u.email ?? "",
        }));
        console.log("userOptions sample:", mapped.slice(0, 2));
        setUserOptions(mapped);
      })
      .catch(() => {});

    supportApi
      .getDepartments()
      .then((res) => {
        setDepartments(
          (Array.isArray(res.data?.data) ? res.data.data : []).map((d) => ({
            label: d.title,
            value: d.id,
            owners: d.owners ?? [],
            manualOwners: d.manualOwners ?? [],
          }))
        );
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openNew = () => {
    setForm(empty);
    setManualOwners([]);
    setDialog(true);
  };

  const openSubCatDialog = (category) => {
    setSelectedCategory(category);
    setSubCatForm(emptySubCat);
    setSubCatDialog(true);
    setSubCatLoading(true);
    supportApi
      .getSubCategories(category.id)
      .then((res) =>
        setSubCategories(Array.isArray(res.data?.data) ? res.data.data : []),
      )
      .catch(() => toast.error("Failed to load sub-categories"))
      .finally(() => setSubCatLoading(false));
  };

  const onSubCatSave = async () => {
    if (!subCatForm.name.trim()) {
      toast.warn("Name is required");
      return;
    }
    setSubCatSaving(true);
    try {
      if (subCatForm.id) {
        await supportApi.updateSubCategory(subCatForm.id, subCatForm);
        toast.success("Sub-category updated");
      } else {
        await supportApi.createSubCategory({
          ...subCatForm,
          categoryId: selectedCategory.id,
        });
        toast.success("Sub-category created");
      }
      setSubCatForm(emptySubCat);
      const res = await supportApi.getSubCategories(selectedCategory.id);
      setSubCategories(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      toast.error("Failed to save sub-category");
    } finally {
      setSubCatSaving(false);
    }
  };

  const onSubCatDelete = async (id) => {
    if (!window.confirm("Deactivate this sub-category?")) return;
    try {
      await supportApi.deleteSubCategory(id);
      toast.success("Sub-category deactivated");
      const res = await supportApi.getSubCategories(selectedCategory.id);
      setSubCategories(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      toast.error("Failed to deactivate");
    }
  };

 const openEdit = (row) => {
  const ids = [];

  (row.owners ?? []).forEach((o) => {
    if (typeof o === "object") {
      if (o.id) ids.push(Number(o.id));
    } else if (typeof o === "number") {
      ids.push(Number(o));
    }
  });

  setForm({
    ...row,
    owners: ids,
  });

  setManualOwners(row.manualOwners || []);

  setDialog(true);
};

  const onSave = async () => {
    if (!form.name.trim()) {
      toast.warn("Name is required");
      return;
    }

    const payload = {
      ...form,
      owners: form.owners || [],
      manualOwners: manualOwners || [],
    };

    setSaving(true);

    try {
      if (form.id) {
        await supportApi.updateCategory(form.id, payload);
        toast.success("Category updated");
      } else {
        await supportApi.createCategory(payload);
        toast.success("Category created");
      }

      setDialog(false);
      setManualOwners([]);
      load();
    } catch {
      toast.error("Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Deactivate this category?")) return;
    try {
      await supportApi.deleteCategory(id);
      toast.success("Category deactivated");
      load();
    } catch {
      toast.error("Failed to deactivate");
    }
  };

  const deptIds = departmentId ? String(departmentId).split(",").map(Number).filter(Boolean) : [];
  const filtered = categories.filter((c) => {
    const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase());
    const matchDept = !deptIds.length || deptIds.includes(c.departmentId);
    return matchSearch && matchDept;
  });

  return (
    <div>
      <TabView>
        <TabPanel header="Categories">
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <div className="search-wrap">
          <i className="pi pi-search" />
          <InputText
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
        </div>
        <Button
          label="Add Category"
          icon="pi pi-plus"
          size="small"
          style={{
            marginLeft: "auto",
            background: "var(--blue)",
            border: "none",
          }}
          onClick={openNew}
        />
      </div>

      <DataTable value={filtered} loading={loading} stripedRows>
        <Column
          header="Icon"
          body={(r) => (
            <i
              className={`pi ${r.icon}`}
              style={{ fontSize: 20, color: "var(--blue)" }}
            />
          )}
          style={{ width: "60px" }}
        />

        <Column
          field="name"
          header="Name"
          style={{ width: "180px", fontWeight: 600 }}
        />

        <Column
          header="Department"
          body={(r) => r.department?.title ?? "—"}
          style={{ width: "140px" }}
        />

        <Column
          field="description"
          header="Description"
          style={{
            fontSize: 12,
            color: "var(--muted)",
            width: "40%",
          }}
        />

        <Column
          field="articleCount"
          header="Articles"
          style={{ width: "90px", textAlign: "center" }}
        />

        <Column
          header="Status"
          body={(r) => (
            <Tag
              value={r.isActive ? "Active" : "Inactive"}
              className={`p-tag ${r.isActive ? "tag-resolved" : "tag-closed"}`}
            />
          )}
          style={{ width: "110px", textAlign: "center" }}
        />

        <Column
          header="Actions"
          style={{ width: "220px" }}
          body={(r) => (
            <div style={{ display: "flex", gap: 4 }}>
              <Button
                label="Edit"
                size="small"
                outlined
                severity="secondary"
                onClick={() => openEdit(r)}
              />
              <Button
                label="Del"
                size="small"
                outlined
                severity="danger"
                onClick={() => onDelete(r.id)}
              />
              <Button
                label="Sub-Cat"
                size="small"
                outlined
                severity="info"
                icon="pi pi-list"
                onClick={() => openSubCatDialog(r)}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header={`Sub-Categories — ${selectedCategory?.name ?? ""}`}
        visible={subCatDialog}
        style={{ width: 860 }}
        onHide={() => setSubCatDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            <InputText
              value={subCatForm.name}
              onChange={(e) =>
                setSubCatForm((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Sub-category name *"
              style={{ width: "100%" }}
            />
            <InputText
              value={subCatForm.description ?? ""}
              onChange={(e) =>
                setSubCatForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Description"
              style={{ width: "100%" }}
            />
            <Button
              label={subCatForm.id ? "Update" : "Add"}
              icon="pi pi-check"
              size="small"
              loading={subCatSaving}
              style={{
                background: "var(--blue)",
                border: "none",
                whiteSpace: "nowrap",
              }}
              onClick={onSubCatSave}
            />
            {subCatForm.id && (
              <Button
                label="Cancel"
                size="small"
                outlined
                severity="secondary"
                onClick={() => setSubCatForm(emptySubCat)}
              />
            )}
          </div>
          <DataTable
            value={subCategories}
            loading={subCatLoading}
            stripedRows
            emptyMessage="No sub-categories yet"
            style={{ width: "100%" }}
          >
            <Column
              field="name"
              header="Name"
              style={{ fontWeight: 600, minWidth: 180 }}
            />
            <Column
              field="description"
              header="Description"
              style={{ fontSize: 12, minWidth: 220 }}
            />
            <Column
              header="Actions"
              style={{ width: 140 }}
              body={(r) => (
                <div style={{ display: "flex", gap: 4 }}>
                  <Button
                    label="Edit"
                    size="small"
                    outlined
                    severity="secondary"
                    onClick={() =>
                      setSubCatForm({
                        id: r.id,
                        name: r.name,
                        description: r.description ?? "",
                      })
                    }
                  />
                  <Button
                    label="Del"
                    size="small"
                    outlined
                    severity="danger"
                    onClick={() => onSubCatDelete(r.id)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </Dialog>

      <Dialog
        header={form.id ? "Edit Category" : "New Category"}
        visible={dialog}
        style={{ width: 480 }}
        onHide={() => setDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Department *
            </label>
            <Dropdown
              value={form.departmentId}
              options={departments}
              onChange={(e) => set("departmentId", e.value)}
              placeholder="Select department…"
              filter
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Name *
            </label>
            <InputText
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              style={{ width: "100%" }}
              placeholder="Category name"
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Owner
            </label>
            <MultiSelect
              value={form.owners ?? []}
              options={userOptions}
              onChange={(e) => set("owners", e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select owners…"
              filter
              filterPlaceholder="Search people…"
              style={{ width: "100%" }}
              selectedItemTemplate={(value) => {
                const user = userOptions.find((u) => u.value === value);
                if (!user) return null;
                return (
                  <span
                    style={{
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 20,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                      marginRight: 4,
                      color: "#1e3a5f",
                    }}
                  >
                    {user.label}
                  </span>
                );
              }}
            />
            <Chips
              value={manualOwners}
              onChange={(e) => {
                const validEmails = e.value.filter(isValidEmail);
                setManualOwners(validEmails);
              }}
              placeholder="Add email and press enter"
              style={{ width: "100%", marginTop: 6 }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 8,
              }}
            >
              Icon
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {ICON_OPTIONS.map((ic) => (
                <div
                  key={ic}
                  onClick={() => set("icon", ic)}
                  style={{
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 6,
                    cursor: "pointer",
                    border: `2px solid ${form.icon === ic ? "var(--blue)" : "var(--border)"}`,
                    background:
                      form.icon === ic ? "var(--blue-light, #eff6ff)" : "#fff",
                  }}
                >
                  <i
                    className={`pi ${ic}`}
                    style={{
                      color: form.icon === ic ? "var(--blue)" : "var(--muted)",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Description
            </label>
            <InputTextarea
              value={form.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              style={{ width: "100%" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 4,
            }}
          >
            <Button
              label="Cancel"
              outlined
              severity="secondary"
              onClick={() => setDialog(false)}
            />
            <Button
              label="Save"
              icon="pi pi-save"
              loading={saving}
              style={{ background: "var(--blue)", border: "none" }}
              onClick={onSave}
            />
          </div>
        </div>
      </Dialog>
        </TabPanel>

        {/* ── Department Owners Tab ── */}
        <TabPanel header="Department Owners">
          <DataTable value={departments} stripedRows emptyMessage="No departments">
            <Column field="label" header="Department" style={{ fontWeight: 600 }} />
            <Column
              header="Owners"
              body={(d) => {
                const ownerLabels = (d.owners ?? []).map((oid) => {
                  const u = userOptions.find((u) => u.value === Number(oid));
                  return u ? u.label : `#${oid}`;
                });
                const all = [...ownerLabels, ...(d.manualOwners ?? [])];
                return all.length
                  ? <span style={{ fontSize: 12 }}>{all.join(", ")}</span>
                  : <span style={{ color: "var(--muted)", fontSize: 12 }}>No owners set</span>;
              }}
            />
            <Column
              header="Actions"
              style={{ width: 100 }}
              body={(d) => (
                <Button
                  label="Edit"
                  size="small"
                  outlined
                  severity="secondary"
                  onClick={() => openDeptOwnerEdit({ id: d.value, title: d.label, owners: d.owners, manualOwners: d.manualOwners })}
                />
              )}
            />
          </DataTable>
        </TabPanel>
      </TabView>

      {/* Department Owner Edit Dialog */}
      <Dialog
        header={`Owners — ${deptOwnerForm.title}`}
        visible={deptOwnerDialog}
        style={{ width: 480 }}
        onHide={() => setDeptOwnerDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 5 }}>
              Owner (System Users)
            </label>
            <MultiSelect
              value={deptOwnerForm.owners}
              options={userOptions}
              onChange={(e) => setDeptOwnerForm((p) => ({ ...p, owners: e.value }))}
              optionLabel="label"
              optionValue="value"
              placeholder="Select owners…"
              filter
              filterPlaceholder="Search people…"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 5 }}>
              External Email Owners
            </label>
            <Chips
              value={deptManualOwners}
              onChange={(e) => setDeptManualOwners(e.value.filter(isValidEmail))}
              placeholder="Add email and press Enter"
              style={{ width: "100%" }}
            />
            <small style={{ color: "var(--muted)", fontSize: 11 }}>Only valid email addresses are accepted</small>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button label="Cancel" outlined severity="secondary" onClick={() => setDeptOwnerDialog(false)} />
            <Button
              label="Save"
              icon="pi pi-save"
              loading={deptOwnerSaving}
              style={{ background: "var(--blue)", border: "none" }}
              onClick={onDeptOwnerSave}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default CategoryMaster;
